// System dependencies

import * as z from "zod";

// Application dependencies

import { AuthFields } from "@/_auth/interfaces/auth.interfaces";
import {
  AuthorisationFields,
  AuthorisationLevel,
  AuthorisationNonPortalRoles,
  AuthorisationProduct,
  AuthorisationProductDetails,
  AuthorisationRole,
} from "@/interfaces/authorisation/authorisation.interfaces";
import { BranchFields } from "@/interfaces/authorisation/branch.interfaces";
import { CompanyFields } from "@/interfaces/authorisation/company.interfaces";
import {
  ConfigurationDeploymentTypeCode,
  ConfigurationFields,
  ConfigurationType,
} from "@/interfaces/authorisation/configuration.interfaces";
import { CountryFields } from "@/interfaces/authorisation/country.interfaces";
import { ProductFields } from "@/interfaces/authorisation/product.interfaces";
import { StateFields } from "@/interfaces/authorisation/state.interfaces";
import { TimeZoneFields } from "@/interfaces/authorisation/timeZone.interfaces";
import { isRoleAllowedForProduct } from "@/lib/functions";

// react-hook-form and zod schemas

export const AuthorisationsDetailSchema = z
  .object({
    [AuthorisationFields.Active]: z.boolean(),
    [AuthorisationFields.Branch]: z.string().optional(),
    [AuthorisationFields.Company]: z.string().optional(),
    [AuthorisationFields.Email]: z
      .string()
      .email({ message: "email address is invalid" })
      .min(1, "you must enter an email address"),
    [AuthorisationFields.ExternalRef]: z.string().optional(),
    [AuthorisationFields.Level]: z.nativeEnum(AuthorisationLevel),
    [AuthorisationFields.Product]: z
      .string()
      .min(1, "you must select a product"),
    [AuthorisationFields.Role]: z.nativeEnum(AuthorisationRole),
  })
  .superRefine((data, ctx) => {
    // ----------------------------------------------------------------------------------------------
    // Check company.
    // ----------------------------------------------------------------------------------------------

    if (AuthorisationNonPortalRoles.includes(data.role)) {
      if (!data.company || data.company.length < 1) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "you must select a company",
          path: ["company"],
        });
      }
    }

    // ----------------------------------------------------------------------------------------------
    // Check branch.
    // ----------------------------------------------------------------------------------------------

    if (
      AuthorisationNonPortalRoles.includes(data.role) &&
      data.level === AuthorisationLevel.BRANCH
    ) {
      if (!data.branch || data.branch.length < 1) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "you must select a branch",
          path: ["branch"],
        });
      }
    }

    // ----------------------------------------------------------------------------------------------
    // Check product for non portal and portal users.
    // ----------------------------------------------------------------------------------------------

    const productName = Object.values(AuthorisationProductDetails).find(
      (details) => details[AuthorisationFields.Title] === data.product
    )?.[AuthorisationFields.Name];

    if (!productName) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Invalid product title: ${data.product}`,
      });
      return;
    }

    if (!isRoleAllowedForProduct(data.role, productName)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "The role is not allowed for the selected product.",
        path: ["product"],
      });
    }
  });

export const BranchesDetailSchema = z.object({
  [BranchFields.ContactTimeMessage]: z
    .string()
    .min(1, "you must enter a contact time message"),
  [BranchFields.Country]: z.string().min(1, "you must select a country"),
  [BranchFields.Email]: z
    .string()
    .email({ message: "email address is invalid" })
    .min(1, "you must enter an email address"),
  [BranchFields.Key]: z.string().min(1, "you must enter a key"),
  [BranchFields.Location]: z.string(),
  [BranchFields.Name]: z.string().min(1, "you must enter a name"),
  [BranchFields.NormalAddress]: z.string().min(1, "you must enter an address"),
  [BranchFields.PostalAddress]: z.string(),
  [BranchFields.State]: z.string().min(1, "you must select a state"),
  [BranchFields.Telephone]: z
    .string()
    .min(1, "you must enter a telephone")
    .regex(/^\d{10,15}$/, {
      message: "telephone should contain only 10 to 15 digits",
    }),
  [BranchFields.TimeZone]: z.string().min(1, "you must select a time zone"),
  [BranchFields.Zone]: z.string().min(1, "you must select a zone"),
});

export const CompaniesDetailSchema = z.object({
  [CompanyFields.Country]: z.string().min(1, "you must select a country"),
  [CompanyFields.Email]: z
    .string()
    .email({ message: "email address is invalid" })
    .min(1, "you must enter an email address"),
  [CompanyFields.Key]: z.string().min(1, "you must enter a key"),
  [CompanyFields.Name]: z.string().min(1, "you must enter a name"),
  [CompanyFields.NormalAddress]: z.string().min(1, "you must enter an address"),
  [CompanyFields.PostalAddress]: z.string(),
  [CompanyFields.State]: z.string().min(1, "you must select a state"),
  [CompanyFields.Telephone]: z
    .string()
    .min(1, "you must enter a telephone")
    .regex(/^\d{10,15}$/, {
      message: "telephone should contain only 10 to 15 digits",
    }),
  [CompanyFields.TimeZone]: z.string().min(1, "you must select a time zone"),
  [CompanyFields.Zone]: z.string().min(1, "you must select a zone"),
});

export const ConfigurationTypeSchema = z.object({
  [ConfigurationFields.Type]: z.nativeEnum(ConfigurationType, {
    required_error: "you must select a type",
    invalid_type_error: "you must select a valid type",
  }),
});

export const ConfigurationDetailAutoItSchema = z.object({
  [ConfigurationFields.AccessKey]: z
    .string()
    .min(1, "you must enter an access key"),
  [ConfigurationFields.BaseUrl]: z.string().url("you must enter a valid URL"),
  [ConfigurationFields.DatabaseId]: z.coerce
    .number({
      required_error: "you must enter a database ID",
      invalid_type_error: "database ID must be a number",
    })
    .int("database ID must be a whole number")
    .gt(0, "database ID must be greater than 0"),
  [ConfigurationFields.DeploymentTypeCode]: z.nativeEnum(
    ConfigurationDeploymentTypeCode,
    {
      required_error: "you must select a deployment type code",
      invalid_type_error: "deployment type code is invalid",
    }
  ),
  [ConfigurationFields.SecretKey]: z
    .string()
    .min(1, "you must enter a secret key"),
  [ConfigurationFields.TypeDescription]: z.string(),
});

export const ConfigurationDetailPartsPortalSchema = z.object({
  [ConfigurationFields.TypeDescription]: z
    .string()
    .url("you must enter a type description"),
});

export const CountriesDetailSchema = z.object({
  [CountryFields.Abbrev2]: z.string().length(2, "must be 2 characters"),
  [CountryFields.Abbrev3]: z.string().length(3, "must be 3 characters"),
  [CountryFields.Name]: z.string().min(1, "you must enter a name"),
  [CountryFields.NumericCode]: z
    .string()
    .length(3, "must be a 3 digit zero padded number")
    .regex(/^\d{3}$/, "must be a 3 digit zero padded number"),
  [CountryFields.StateName]: z.string().min(1, "you must enter a state name"),
});

export const ForgotSchema = z.object({
  [AuthFields.Email]: z
    .string()
    .email("must be a valid email address")
    .min(1, "email is required"),
});

export const ProductsDetailSchema = z.object({
  [ProductFields.Name]: z.nativeEnum(AuthorisationProduct),
  [ProductFields.Title]: z.string().min(1, "you must enter a title"),
});

export const ProfileHeaderSchema = z.object({
  [AuthFields.FirstName]: z.string().min(1, "first name is required"),
  [AuthFields.LastName]: z.string().min(1, "last name is required"),
});

export const ProfilePasswordSchema = z
  .object({
    [AuthFields.ConfirmPassword]: z
      .string()
      .min(1, "confirm password is required"),
    [AuthFields.Password]: z.string().min(1, "password is required"),
  })
  .refine(
    (data) => data[AuthFields.ConfirmPassword] === data[AuthFields.Password],
    {
      message: "passwords must match",
      path: [AuthFields.ConfirmPassword],
    }
  );

export const SignInSchema = z.object({
  [AuthFields.Email]: z
    .string()
    .email("must be a valid email address")
    .min(1, "email is required"),
  [AuthFields.Password]: z.string().min(1, "password is required"),
});

export const SignUpSchema = z
  .object({
    [AuthFields.ConfirmPassword]: z
      .string()
      .min(1, "confirm password is required"),
    [AuthFields.Email]: z
      .string()
      .email("must be a valid email address")
      .min(1, "email is required"),
    [AuthFields.FirstName]: z.string().min(1, "first name is required"),
    [AuthFields.LastName]: z.string().min(1, "last name is required"),
    [AuthFields.Password]: z.string().min(1, "password is required"),
  })
  .refine(
    (data) => data[AuthFields.ConfirmPassword] === data[AuthFields.Password],
    {
      message: "passwords must match",
      path: [AuthFields.ConfirmPassword],
    }
  );

export const StateCountrySchema = z.object({
  [StateFields.Country]: z.string().min(1, "you must select a country"),
});

export const StatesDetailSchema = z.object({
  [StateFields.Code]: z.string().min(1, "you must enter a code"),
  [StateFields.Country]: z.string().min(1, "you must select a country"),
  [StateFields.NameLong]: z.string().min(1, "you must enter a long name"),
  [StateFields.NameShort]: z.string().min(1, "you must enter a short name"),
  [StateFields.TimeZone]: z.string().min(1, "you must select a time zone"),
  [StateFields.Zone]: z.string().min(1, "you must select a zone"),
});

export const TimeZoneCountrySchema = z.object({
  [TimeZoneFields.Country]: z.string().min(1, "you must select a country"),
});

export const TimeZoneDetailSchema = z.object({
  [TimeZoneFields.Country]: z.string().min(1, "you must select a country"),
  [TimeZoneFields.Description]: z
    .string()
    .min(1, "you must enter a description"),
  [TimeZoneFields.Zone]: z
    .string()
    .min(1, "you must enter a time zone identifier"),
});

export const TotpInputSchema = z.object({
  [AuthFields.TotpCode]: z.string().min(6, {
    message: "one-time passcodes must be 6 characters",
  }),
});
